import type {
  GetApiV1UserMe200,
  PermissionApiDTO,
  UserWithRolesApiDTO,
} from '@testsa-com/common/src/codegen/generated'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { UsersHelper } from '../helpers/Users.helper'

type UserStoreType = {
  me: GetApiV1UserMe200 | undefined
  isAuthorised: (permission: PermissionApiDTO | PermissionApiDTO[]) => boolean
  isAuthorisedOneOf: (permissions: PermissionApiDTO[]) => boolean
  reset: () => void
}

const AUTH_KEY = 'tsa-user'

export const useUserStore = create(
  persist<UserStoreType>(
    (set, get, store) => ({
      me: undefined,
      isAuthorised: permission => {
        const { me } = get()

        if (!(me as UserWithRolesApiDTO)?.roles) {
          return false
        }

        // Specific permissions
        if (permission === 'projects.admin' || permission === 'projects.delete') {
          return (me as UserWithRolesApiDTO)?.roles?.some(
            r => r.id === UsersHelper.SUPER_ADMIN_ROLE,
          )
        }

        // Admin
        if (
          (me as UserWithRolesApiDTO)?.roles?.some(
            r => r.id === UsersHelper.ADMIN_ROLE || r.id === UsersHelper.SUPER_ADMIN_ROLE,
          )
        ) {
          return true
        }

        if (typeof permission === 'string') {
          return !!(me as UserWithRolesApiDTO)?.roles?.some(r => r.permissions.includes(permission))
        }

        return permission.reduce(
          (_, perm) =>
            !!(me as UserWithRolesApiDTO)?.roles?.some(r => r.permissions.includes(perm)),
          false,
        )
      },
      isAuthorisedOneOf: permissions => {
        const { me } = get()

        // Specific permissions
        if (permissions.includes('projects.admin') || permissions.includes('projects.delete')) {
          return (me as UserWithRolesApiDTO)?.roles?.some(
            r => r.id === UsersHelper.SUPER_ADMIN_ROLE,
          )
        }

        // Admin
        if (
          (me as UserWithRolesApiDTO)?.roles?.some(
            r => r.id === UsersHelper.ADMIN_ROLE || r.id === UsersHelper.SUPER_ADMIN_ROLE,
          )
        ) {
          return true
        }

        return permissions.some(
          p => !!(me as UserWithRolesApiDTO)?.roles?.some(r => r.permissions.includes(p)),
        )
      },
      reset: () => {
        localStorage.removeItem(AUTH_KEY)
        set(() => store.getInitialState())
      },
    }),
    { name: AUTH_KEY },
  ),
)
